import { memo } from "react";
import SectionNavigationList from "../section-navigation-list/section-navigation-list";
import SectionNavigationListItem from "../section-navigation-list-item/section-navigation-list-item";
import { SectionNavigationListItemProps } from "../section-navigation-list-item/interfaces";
import { destructureItem } from "./helpers";
import { ItemContentProps, ListBuilderProps } from "./interfaces";

const DefaultListItem = memo(
  <T extends object>(
    props: SectionNavigationListItemProps & ItemContentProps<T>,
  ) => {
    const { item, label, subsections, index, ...rest } = props;
    return <SectionNavigationListItem {...rest} />;
  },
);

const ListBuilder = <T extends object>(props: ListBuilderProps<T>) => {
  const {
    items,
    index: parentIndex = 1,
    isSubsection = false,
    ItemContent,
    ListItem = DefaultListItem,
    List = SectionNavigationList,
    getLabel,
    getId,
    getSubsections,
    level = 0,
    renderLevel,
    getters,
    ...rest
  } = props;

  if (level > renderLevel) {
    return null;
  }

  return (
    <List index={parentIndex} isSubsection={isSubsection}>
      {items.map((item: T, itemIndex: number) => {
        const { id, label, subsections } = destructureItem(item, getters);

        const hasSubsections = subsections.length > 0;

        return (
          <ListItem
            key={id}
            childListIndex={`${parentIndex}-${itemIndex + 1}`}
            index={`${parentIndex}-${itemIndex + 1}`}
            hasSubsections={hasSubsections}
            id={id}
            label={label}
            subsections={subsections}
            item={item}
          >
            <ItemContent
              id={id}
              label={label}
              index={`${parentIndex}-${itemIndex + 1}`}
              subsections={subsections}
              item={item}
              {...rest}
            />
            {hasSubsections && (
              <ListBuilder
                items={subsections}
                index={`${parentIndex}-${itemIndex + 1}`}
                ItemContent={ItemContent}
                List={List}
                ListItem={ListItem}
                isSubsection
                getLabel={getLabel}
                getId={getId}
                getSubsections={getSubsections}
                level={level + 1}
                renderLevel={renderLevel}
                getters={getters}
              />
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

// React.memo breaks generics
export default memo(ListBuilder) as typeof ListBuilder;
